import * as React from 'react';
import { Dialog } from '@ampm-7en-ai/ui/components/Dialog';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { useToast } from '@/hooks/use-toast';
import { addonTypeOptions, useCreateAddon, useUpdateAddon, type Addon, type AddonType } from '@/hooks/useAddons';
import { CURRENCY_SYMBOL } from '@/lib/money';

/** An extra a workspace can buy on top of its plan. */
export default function AddonDialogV2({
  open,
  onOpenChange,
  addon,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addon?: Addon | null;
}) {
  const { toast } = useToast();
  const create = useCreateAddon();
  const update = useUpdateAddon();
  const saving = create.isPending || update.isPending;

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [price, setPrice] = React.useState('');
  const [type, setType] = React.useState<AddonType>('AUTO_TICKET_RESPONSE');
  const [active, setActive] = React.useState(true);

  const options = React.useMemo(() => addonTypeOptions(type), [type]);

  React.useEffect(() => {
    if (!open) return;
    setName(addon?.name ?? '');
    setDescription(addon?.description ?? '');
    setPrice(addon?.price_monthly ?? '');
    setType(addon?.addon_type ?? 'AUTO_TICKET_RESPONSE');
    setActive((addon?.status ?? 'ACTIVE') === 'ACTIVE');
  }, [open, addon]);

  const monthly = Number.parseFloat(price);
  const blocked = !name.trim() || !(monthly >= 0);

  const save = async () => {
    if (blocked) return;
    // The API takes a number here, though it hands one back as a string.
    const payload = {
      name: name.trim(),
      description: description.trim(),
      price_monthly: monthly,
      addon_type: type,
      status: (active ? 'ACTIVE' : 'INACTIVE') as 'ACTIVE' | 'INACTIVE',
    };
    try {
      if (addon) await update.mutateAsync({ id: addon.id, ...payload });
      else await create.mutateAsync(payload);
      toast({ title: `${payload.name} ${addon ? 'updated' : 'created'}` });
      onOpenChange(false);
    } catch (failure) {
      toast({
        title: 'That didn’t save',
        description: failure instanceof Error ? failure.message : 'The add-on wasn’t saved.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={addon ? 'Edit add-on' : 'New add-on'}
      description="Billed every month, beside whatever plan the workspace is on."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || blocked}>
            {saving ? 'Saving…' : addon ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="addon-name">
          <Input id="addon-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Extra agent" />
        </Field>

        <Field label="Description" htmlFor="addon-description" hint="What the workspace is buying, in a line.">
          <Textarea
            id="addon-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One more agent beyond what the plan includes."
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="What it is" htmlFor="addon-type" hint="This decides what it actually unlocks.">
            <Select
              id="addon-type"
              options={options}
              value={type}
              onValueChange={(value) => setType(value)}
            />
          </Field>
          <Field label={`Monthly price (${CURRENCY_SYMBOL})`} htmlFor="addon-price">
            <Input
              id="addon-price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="19.00"
            />
          </Field>
        </div>

        <ToggleRow
          label="Available to buy"
          description="Workspaces already on it keep it; switched off, nobody new can add it."
          checked={active}
          onCheckedChange={setActive}
        />
      </div>
    </Dialog>
  );
}
